import setedigitalApi from './api/setedigitalApi';
import { ICategory, INoticia } from '../types/noticia';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Cache para armazenar resultados de requisições recentes
const cache = {
  featured: {
    data: null as INoticia[] | null,
    timestamp: 0,
    expiry: 5 * 60 * 1000, // 5 minutos
  },
  categories: {
    data: null as ICategory[] | null,
    timestamp: 0,
    expiry: 10 * 60 * 1000, // 10 minutos
  },
};

export const noticiaService = {
  /**
   * Buscar todas as categorias de notícias com a contagem de notícias por categoria
   */
  async fetchCategorias(): Promise<ICategory[]> {
    try {
      // Verifica se já temos dados válidos em cache
      const now = Date.now();
      if (
        cache.categories.data &&
        now - cache.categories.timestamp < cache.categories.expiry
      ) {
        return cache.categories.data;
      }

      const response = await setedigitalApi.get('/news/countByCategory');
  
      const categoriasComTotal = response.data.data.map((item: {
        _id: string;
        categoryName: string;
        categorySlug: string;
        count: number;
      }) => ({
        _id: item._id,
        title: item.categoryName,
        categorySlug: item.categorySlug,
        totalDocs: item.count
      }));
      
      // Atualiza o cache
      cache.categories.data = categoriasComTotal;
      cache.categories.timestamp = now;
  
      return categoriasComTotal;
    } catch (error: any) {
      console.error('Erro ao buscar categorias:', error);
      // Retorna o cache mesmo que esteja expirado, em caso de erro
      return cache.categories.data || [];
    }
  },
  
  /**
   * Buscar todas as categorias de forma simples (sem contagem de notícias)
   */
  async fetchCategoriasSimples(): Promise<ICategory[]> {
    try {
      const response = await setedigitalApi.get(`/category/readAll?limit=${Number.MAX_SAFE_INTEGER}`);
      return response.data.data.docs;
    } catch (error) {
      console.error('Erro ao buscar categorias simples:', error);
      return [];
    }
  },

  /**
   * Buscar notícias em destaque com um limite opcional
   */
  async fetchFeaturedNoticias(limit?: number): Promise<INoticia[]> {
    try {
      const now = Date.now();
      if (
        cache.featured.data &&
        now - cache.featured.timestamp < cache.featured.expiry
      ) {
        // Remover o slice fixo aqui
        return limit ? cache.featured.data.slice(0, limit) : cache.featured.data;
      }

      // Remover o limit=3 da query string
      const query = `/news/readAll?featured=true&fields=title,descriptionSmall,slug,thumbImage,publishDate,createdAt`;
      const finalQuery = limit ? `${query}&limit=${limit}` : query;
      
      const response = await setedigitalApi.get(finalQuery);

      const DEFAULT_THUMB = '/images/emptyImages/empty-news-img.webp';
      
      const noticiasFormatadas = response.data.data.docs.map((noticia: INoticia) => ({
        ...noticia,
        thumbImage: {
          url: noticia.thumbImage?.url || DEFAULT_THUMB
        }
      }));

      // Remover o slice fixo aqui também
      const noticiasFinais = limit ? noticiasFormatadas.slice(0, limit) : noticiasFormatadas;

      cache.featured.data = noticiasFinais;
      cache.featured.timestamp = now;

      return noticiasFinais;
    } catch (error) {
      console.error('Erro ao buscar notícias destacadas:', error);
      // Remover o slice fixo no fallback também
      return limit ? (cache.featured.data || []).slice(0, limit) : (cache.featured.data || []);
    }
  },



  /**
   * Buscar notícias com filtros (busca, categoria, paginação, etc.)
   */
  async fetchNoticias({ 
    busca = '', 
    categoria = '', 
    page = 1, 
    limit = 6 
  }: { 
    busca?: string; 
    categoria?: string; 
    page?: number; 
    limit?: number; 
  }) {
    try {
      // Monta os parâmetros da URL de forma eficiente
      const params = new URLSearchParams();
      
      if (busca) {
        params.append('query', busca);
        params.append('name', busca);
      }
      
      if (categoria) {
        params.append('category', categoria);
      }
      
      params.append('limit', limit.toString());
      params.append('page', page.toString());
      
      const endpoint = `/news/search?${params.toString()}`;
      const response = await setedigitalApi.get(endpoint);
      
      return {
        noticias: response.data.data.docs,
        hasNextPage: response.data.data.hasNextPage,
        totalDocs: response.data.data.totalDocs,
        page: response.data.data.page
      };
    } catch (error) {
      console.error('Erro ao buscar notícias:', error);
      return {
        noticias: [],
        hasNextPage: false,
        totalDocs: 0,
        page: page
      };
    }
  },

  /**
   * Buscar uma notícia específica pelo slug
   */
  async fetchNoticiaPorSlug(slug: string): Promise<INoticia | null> {
    try {
      const response = await setedigitalApi.get(`/news/readBySlug/${slug}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar notícia com slug ${slug}:`, error);
      return null;
    }
  },

  /**
   * Buscar uma notícia específica pelo ID
   */
  async fetchNoticiaPorId(id: string): Promise<INoticia | null> {
    try {
      const response = await setedigitalApi.get(`/news/readById/${id}?fields=slug`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar notícia com ID ${id}:`, error);
      return null;
    }
  },

  /**
   * Buscar as últimas notícias adicionadas (não ordenadas)
   */
  async fetchNoticiasRecentes(quantidade: number = 4): Promise<INoticia[]> {
    try {
      const response = await setedigitalApi.get('/news/readAll');
      return response.data.data.docs.slice(-quantidade); // Pega os últimos N posts
    } catch (error) {
      console.error('Erro ao buscar notícias recentes:', error);
      return [];
    }
  },

  /**
   * Buscar as últimas notícias ordenadas pela data de criação (mais novas primeiro)
   */
  async fetchUltimasNoticias(limit: number = 4): Promise<INoticia[]> {
    try {
      const response = await setedigitalApi.get(
        `/news/search?limit=${limit}&sortBy=publishDate&order=desc&fields=title,slug,content,descriptionSmall,publishDate,createdAt,thumbImage`
      );
      
      if (response.data && response.data.data.docs) {
        return response.data.data.docs;
      }

      throw new Error('Resposta inválida da API');
    } catch (error) {
      console.error('Erro ao buscar últimas notícias:', error);
      return [];
    }
  },

  /**
   * Formatar uma data para o padrão brasileiro
   */
  formatarData: (data: string | Date) => {
    if (!data) return 'Data indisponível';
    
    try {
      // Tenta formatar usando date-fns
      return `${format(new Date(data), 'dd/MM/yyyy', { locale: ptBR })}, há ${formatDistanceToNow(new Date(data), { addSuffix: false, locale: ptBR })}`;
    } catch (error) {
      // Caso falhe, faz um fallback usando métodos nativos
      try {
        return `${new Date(data).toLocaleDateString('pt-BR')}, ${new Date(data).toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
        })}`;
      } catch (e) {
        return 'Data inválida';
      }
    }
  },
};


export default noticiaService;
